import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { format } from "date-fns";
import axios from "axios";
import BASE_URL from "../config";
import { ToastContainer, toast } from "react-toastify"; // Import Toastify
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import Loader from "../components/Loader"; // Import Loader component

const ReoccurringTasks = () => {
  const { register, handleSubmit, watch, reset, setValue } = useForm();
  const [startDate, setStartDate] = useState(new Date());
  const [selectedDays, setSelectedDays] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [loading, setLoading] = useState(true); // Add loading state
  const navigate = useNavigate(); // Initialize navigate

  const repeatType = watch("repeat_type");
  const endType = watch("end_type");

  // ✅ Fetch active users
  useEffect(() => {
    const fetchAssignees = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await axios.get(`${BASE_URL}api/user/active-users`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const mapped = res.data.map((user) => {
          let roleLabel = "";
          switch (user.role) {
            case "user":
              roleLabel = "Software Engineer";
              break;
            case "admin":
              roleLabel = "CTO-Admin";
              break;
            case "tester":
              roleLabel = "Tester";
              break;
            default:
              roleLabel = user.role;
          }

          return {
            userId: user.id, // Save user ID
            value: user.username, // Save username
            label: `${user.username} - ${roleLabel}`,
          };
        });

        setAssignees(mapped); // Set the updated assignees array
      } catch (err) {
        console.error("Failed to load users", err);
      } finally {
        setLoading(false); // Set loading to false after data is fetched
      }
    };

    fetchAssignees();
  }, []);

  const handleDayChange = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  const onSubmit = async (data) => {
    setLoading(true); // Set loading to true during form submission
    const newTask = {
      title: data.task_name,
      project: "Recurring Tasks",
      details: data.description,
      assignee: data.assignee,
      date: format(startDate, "yyyy-MM-dd"),
      list: "TODO",
      color: "#0d6efd",
      textClass: "text-primary",
      repeat_type: data.repeat_type,
      repeat_interval: data.repeat_interval || null,
      // Ensure selectedDays is correctly sent as a JSON string if it's not empty
      days_of_week: selectedDays.length ? JSON.stringify(selectedDays) : null,
      end_type: data.end_type,
      end_date: data.end_date || null,
      max_occurrences: data.max_occurrences || null,
    };

    console.log("Payload before submit:", newTask); // Log the payload

    try {
      const response = await fetch(`${BASE_URL}api/tasks/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newTask),
      });

      if (response.ok) {
        await response.json();
        toast.success("Task created successfully!"); // Show success toaster
        setTimeout(() => navigate("/admin-reoccurring"), 2000); // Navigate back after 2 seconds
        reset();
      } else {
        toast.error("Something went wrong!"); // Show error toaster
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("Something went wrong!"); // Show error toaster
    } finally {
      setLoading(false); // Set loading to false after submission
    }
  };

  return (
    <div className="container d-flex justify-content-between min-vh-100">
      {loading ? (
        <Loader message="Loading, please wait..." />
      ) : (
        <div className="col-md-12 bg-white p-4">
          <h2 className="mb-4">Create Recurring Task</h2>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Task name */}
            <div className="mb-3">
              <label className="form-label fw-semibold">Task Name</label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g., Setup hosting"
                {...register("task_name", { required: true })}
              />
            </div>

            {/* Description */}
            <div className="mb-3">
              <label className="form-label fw-semibold">Description</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Optional details..."
                {...register("description")}
              ></textarea>
            </div>

            {/* Assignee and Start Date */}
            <div className="row">
              <div className="mb-3 col-md-6">
                <label className="form-label fw-semibold">Assign To</label>
                <select
                  className="form-select"
                  {...register("assignee")}
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select assignee
                  </option>
                  {assignees.map((user) => (
                    <option key={user.value} value={user.userId}>
                      {user.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3 col-md-6">
                <label className="form-label fw-semibold">Start Date</label>
                <input
                  type="date"
                  value={format(startDate, "yyyy-MM-dd")}
                  onChange={(e) => setStartDate(new Date(e.target.value))}
                  className="form-control"
                  min={format(new Date(), "yyyy-MM-dd")} // Disable previous dates
                />
              </div>
            </div>

            {/* Repeat type */}
            <div className="mb-3">
              <label className="form-label fw-semibold">Repeat</label>
              <select
                className="form-select"
                {...register("repeat_type")}
                defaultValue="daily"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                {/* <option value="custom">Custom</option> */}
              </select>
            </div>

            {/* Weekly repeat days */}
            {repeatType === "weekly" && (
              <div className="mb-3">
                <label className="form-label fw-semibold">Repeat On</label>
                <div className="d-flex flex-wrap gap-2">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day) => (
                      <div key={day} className="form-check me-3">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          value={day}
                          onChange={() => handleDayChange(day)}
                          checked={selectedDays.includes(day)} // Ensure it properly reflects the selected state
                        />
                        <label className="form-check-label">{day}</label>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Custom interval */}
            {repeatType === "custom" && (
              <div className="mb-3">
                <label className="form-label fw-semibold">Repeat Every</label>
                <input
                  type="number"
                  min="1"
                  defaultValue="1"
                  className="form-control"
                  {...register("repeat_interval")}
                />
              </div>
            )}

            {/* End type */}
            <div className="mb-3">
              <label className="form-label fw-semibold">End Type</label>
              <select
                className="form-select"
                {...register("end_type")}
                defaultValue="never"
              >
                <option value="never">Never</option>
                <option value="on_date">On a Specific Date</option>
                {/* <option value="after_occurrences">After X Occurrences</option> */}
              </select>
            </div>

            {/* End date */}
            {endType === "on_date" && (
              <div className="mb-3">
                <label className="form-label fw-semibold">End Date</label>
                <input
                  type="date"
                  className="form-control"
                  {...register("end_date")}
                />
              </div>
            )}

            {/* Max occurrences */}
            {endType === "after_occurrences" && (
              <div className="mb-3">
                <label className="form-label fw-semibold">Max Occurrences</label>
                <input
                  type="number"
                  className="form-control"
                  {...register("max_occurrences")}
                />
              </div>
            )}

            <button type="submit" className="btn btn-primary w-100 fw-bold mt-3">
              Create Task
            </button>
          </form>
        </div>
      )}
      <ToastContainer /> {/* Add ToastContainer for notifications */}
    </div>
  );
};

export default ReoccurringTasks;
